import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Building2, Smartphone, Landmark } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, StatusBadge, Spinner, ProviderNotice, VerifiedChip } from "../components/common";
import { useAuth } from "../context/AuthContext";

const ICONS = { banking: Building2, moncash: Smartphone };

export default function Withdraw() {
  const { user } = useAuth();
  const verified = !!(user?.identity_verified || (user?.badge_active && user?.badge_color === "blue"));
  const [methods, setMethods] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [setupMsg, setSetupMsg] = useState(null);

  const load = async () => {
    try {
      const [m, s, w] = await Promise.all([
        api.get("/withdraw/methods"), api.get("/withdraw/stripe/status"), api.get("/wallet"),
      ]);
      setMethods(m.data); setStripe(s.data); setWallet(w.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const withdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post("/withdraw/stripe", { amount: amt });
      toast.success("Withdrawal sent to your bank");
      setAmount(""); await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "WITHDRAW_SETUP_REQUIRED") setSetupMsg(d.message);
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const tapOther = (m) => toast.error(`${m.name} is ${m.status_label}. API connection required.`);

  if (loading) return <FullLoader />;

  const ready = stripe?.payouts_enabled;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Withdraw" subtitle="Cash out to your bank" />

      {verified && <div className="mb-4"><VerifiedChip verified testid="withdraw-verified-chip" /></div>}

      <div className="rounded-3xl bg-card border border-border p-5 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Landmark size={18} className="text-primary" />
          <span className="font-display font-semibold">Bank payout (Stripe)</span>
          {ready && <span className="ml-auto"><StatusBadge label="READY" /></span>}
        </div>
        <p className="text-xs text-muted-foreground mb-3">Available balance: <span className="font-semibold text-foreground">{wallet?.available_display}</span></p>

        {ready ? (
          <>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-light text-muted-foreground">$</span>
              <input data-testid="withdraw-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                className="w-full h-16 pl-10 pr-4 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none amount-figure text-3xl" />
            </div>
            <button data-testid="withdraw-submit-btn" onClick={withdraw} disabled={busy}
              className="w-full h-14 mt-4 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-60">
              {busy ? <Spinner className="w-5 h-5" /> : "Withdraw to bank"}
            </button>
            {setupMsg && <p className="text-xs text-warning mt-3">{setupMsg}</p>}
          </>
        ) : (
          <div data-testid="withdraw-coming-soon" className="flex items-start gap-3 rounded-2xl bg-secondary/60 p-4 mt-1">
            <Banknote size={18} className="text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">Bank withdrawals are being activated for your account. Once ready, you'll be able to cash out to your bank right here.</p>
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-primary/5 border border-primary/15 p-4 mb-5 flex gap-3">
        <Banknote size={18} className="text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">Withdrawals move real money and create an auditable ledger entry. Your balance is only reduced once the payout is confirmed.</p>
      </div>

      {methods.filter((m) => m.connected).length > 0 && (
        <div className="space-y-3">
          {methods.filter((m) => m.connected).map((m) => {
            const Icon = ICONS[m.key] || Building2;
            return (
              <button key={m.key} data-testid={`withdraw-method-${m.key}`} onClick={() => tapOther(m)}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-card border border-border text-left transition-transform duration-200 active:scale-[0.98]">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-primary shrink-0"><Icon size={22} /></span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{m.name}</div>
                </div>
                <StatusBadge label={m.status_label} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
